/**
 * svgPathParser.ts
 *
 * SVG `d` path parsing + region extraction, shared by the OOXML slide builder
 * and region-name resolution. Pure logic apart from `DOMParser` (a browser
 * global available inside the Office webview).
 */

import type { BoundingBox, ExtractedShape, ParsedPathCommand } from "../types";

/** Matches one signed decimal number with optional exponent. */
const NUMBER_RE = /[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g;

/** SVG path command letters. Deliberately excludes e/E (exponent markers). */
const COMMAND_RE = /([MmLlHhVvCcSsQqTtAaZz])/;

/** Tokenize a path `d` string into command letters and numbers. */
function tokenizePathD(d: string): (string | number)[] {
  const tokens: (string | number)[] = [];
  for (const part of d.split(COMMAND_RE)) {
    if (part.length === 1 && COMMAND_RE.test(part)) {
      tokens.push(part);
    } else {
      for (const m of part.matchAll(NUMBER_RE)) tokens.push(Number.parseFloat(m[0]));
    }
  }
  return tokens;
}

/** Mutable cursor state threaded through the path command handlers. */
interface PathParserState {
  cx: number;
  cy: number; // current point
  sx: number;
  sy: number; // subpath start
  pcx: number;
  pcy: number; // previous control point (for S/T)
}

/** Emit a lineTo and advance the cursor. */
function lineTo(st: PathParserState, x: number, y: number, out: ParsedPathCommand[]): void {
  st.cx = x;
  st.cy = y;
  out.push({ t: "L", x, y });
  st.pcx = x;
  st.pcy = y;
}

/** Emit a cubic, advance the cursor, and record the trailing control point. */
function cubicTo(
  st: PathParserState,
  pts: { x1: number; y1: number; x2: number; y2: number; x: number; y: number },
  out: ParsedPathCommand[],
): void {
  st.cx = pts.x;
  st.cy = pts.y;
  out.push({ t: "C", ...pts });
  st.pcx = pts.x2;
  st.pcy = pts.y2;
}

/** Convert a quadratic segment (control qx,qy → end ex,ey) to cubic and emit it. */
function quadToCubic(
  st: PathParserState,
  qx: number,
  qy: number,
  ex: number,
  ey: number,
  out: ParsedPathCommand[],
): void {
  const x1 = st.cx + (2 / 3) * (qx - st.cx);
  const y1 = st.cy + (2 / 3) * (qy - st.cy);
  const x2 = ex + (2 / 3) * (qx - ex);
  const y2 = ey + (2 / 3) * (qy - ey);
  st.cx = ex;
  st.cy = ey;
  out.push({ t: "C", x1, y1, x2, y2, x: st.cx, y: st.cy });
  st.pcx = qx;
  st.pcy = qy;
}

/**
 * Execute one path command, consuming its arguments via `num`. Returns the
 * command to use for an implicit repeat (M/m becomes L/l).
 */
function applyCommand(
  cmd: string,
  st: PathParserState,
  num: () => number,
  out: ParsedPathCommand[],
): string {
  const rel = cmd >= "a" && cmd <= "z";
  const bx = rel ? st.cx : 0;
  const by = rel ? st.cy : 0;

  switch (cmd.toUpperCase()) {
    case "M":
      st.cx = num() + bx;
      st.cy = num() + by;
      out.push({ t: "M", x: st.cx, y: st.cy });
      st.sx = st.cx;
      st.sy = st.cy;
      st.pcx = st.cx;
      st.pcy = st.cy;
      return rel ? "l" : "L"; // implicit repeat → lineTo
    case "L":
      lineTo(st, num() + bx, num() + by, out);
      break;
    case "H":
      lineTo(st, num() + bx, st.cy, out);
      break;
    case "V":
      lineTo(st, st.cx, num() + by, out);
      break;
    case "C":
      cubicTo(
        st,
        {
          x1: num() + bx,
          y1: num() + by,
          x2: num() + bx,
          y2: num() + by,
          x: num() + bx,
          y: num() + by,
        },
        out,
      );
      break;
    case "S":
      cubicTo(
        st,
        {
          x1: 2 * st.cx - st.pcx,
          y1: 2 * st.cy - st.pcy,
          x2: num() + bx,
          y2: num() + by,
          x: num() + bx,
          y: num() + by,
        },
        out,
      );
      break;
    case "Q": {
      const qx = num() + bx;
      const qy = num() + by;
      quadToCubic(st, qx, qy, num() + bx, num() + by, out);
      break;
    }
    case "T":
      quadToCubic(st, 2 * st.cx - st.pcx, 2 * st.cy - st.pcy, num() + bx, num() + by, out);
      break;
    case "A":
      // Arc → straight line approximation (arcs are rare in map SVGs).
      num();
      num();
      num();
      num();
      num(); // rx, ry, rot, largeArc, sweep
      lineTo(st, num() + bx, num() + by, out);
      break;
    case "Z":
      out.push({ t: "Z" });
      st.cx = st.sx;
      st.cy = st.sy;
      st.pcx = st.cx;
      st.pcy = st.cy;
      break;
    default:
      // Unknown/missing command: consume one argument so the caller's
      // implicit-repeat loop always makes progress.
      num();
      break;
  }
  return cmd;
}

/**
 * Parse an SVG path `d` attribute into absolute-coordinate commands.
 * Handles M m L l H h V v C c S s Q q T t A a Z z. Quadratics → cubic; arcs →
 * straight line (rare in map SVGs).
 */
export function parseSvgPathD(d: string | null | undefined): ParsedPathCommand[] {
  if (!d) return [];

  const tokens = tokenizePathD(d);
  const out: ParsedPathCommand[] = [];
  const st: PathParserState = { cx: 0, cy: 0, sx: 0, sy: 0, pcx: 0, pcy: 0 };
  let i = 0;
  let cmd = "";

  const num = (): number => {
    const t = tokens[i];
    if (typeof t === "number") {
      i++;
      return t;
    }
    return 0;
  };
  const moreNums = (): boolean => i < tokens.length && typeof tokens[i] === "number";

  while (i < tokens.length) {
    const tok = tokens[i];
    if (typeof tok === "string") {
      cmd = tok;
      i++;
    } else if (cmd === "M") {
      cmd = "L"; // implicit repeat — after M/m, repeats become L/l
    } else if (cmd === "m") {
      cmd = "l";
    }

    do {
      cmd = applyCommand(cmd, st, num, out);
    } while (cmd.toUpperCase() !== "Z" && moreNums());
  }

  return out;
}

/**
 * Extract per-region shapes from an SVG string. Two formats:
 *   US:    `<g class="state"><path><title>Name</title></path></g>` → title text
 *   World: `<g id="XX">` 2-letter ISO code → name = the ISO code (e.g. "AU")
 */
export function extractShapes(svgString: string): ExtractedShape[] {
  // Security note (SAST): `svgString` is always a first-party map asset shipped
  // in `public/assets/deckorate-assets/svg/` and fetched same-origin — never
  // user input. The browser `DOMParser` with "image/svg+xml" does not resolve
  // external entities (no XXE), and the parsed document is only read via
  // attribute/text accessors below — it is never injected back into the live
  // DOM (no innerHTML / XSS sink).
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const stateGroup = doc.querySelector("g.state");
  return stateGroup ? extractUsShapes(stateGroup) : extractWorldShapes(doc);
}

/** US format: `<g class="state"><path><title>Name</title></path></g>`. */
function extractUsShapes(stateGroup: Element): ExtractedShape[] {
  const shapes: ExtractedShape[] = [];
  for (const path of Array.from(stateGroup.querySelectorAll("path"))) {
    const d = path.getAttribute("d");
    if (!d) continue;
    const titleEl = path.querySelector("title");
    const name = titleEl?.textContent ?? path.getAttribute("class") ?? "State";
    const cmds = parseSvgPathD(d);
    if (cmds.length > 2) shapes.push({ name, cmds });
  }
  return shapes;
}

/** World format: `<g id="XX">` 2-letter ISO code → name = the ISO code. */
function extractWorldShapes(doc: Document): ExtractedShape[] {
  const shapes: ExtractedShape[] = [];
  for (const g of Array.from(doc.querySelectorAll("svg > g"))) {
    const id = g.getAttribute("id");
    if (!id || !/^[A-Z]{2}$/.test(id)) continue;
    const cmds: ParsedPathCommand[] = [];
    for (const path of Array.from(g.querySelectorAll("path"))) {
      const d = path.getAttribute("d");
      if (d) cmds.push(...parseSvgPathD(d));
    }
    if (cmds.length > 2) shapes.push({ name: id, cmds });
  }
  return shapes;
}

/** Conservative bounding box (includes bezier control points). */
export function computeBoundingBox(cmds: ParsedPathCommand[]): BoundingBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const check = (x: number, y: number): void => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  };

  for (const c of cmds) {
    if (c.t === "Z") continue;
    if (c.x !== undefined && c.y !== undefined) check(c.x, c.y);
    if (c.x1 !== undefined && c.y1 !== undefined) check(c.x1, c.y1);
    if (c.x2 !== undefined && c.y2 !== undefined) check(c.x2, c.y2);
  }

  return { minX, minY, maxX, maxY };
}

/** Region/state shape names from an SVG (same extraction the builder uses). */
export function getRegionNamesFromSvg(svgString: string): string[] {
  return extractShapes(svgString)
    .map((s) => s.name)
    .sort((a, b) => a.localeCompare(b));
}
